import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { apiClient } from "@/App";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Car, MapPin, Plus, X, CalendarIcon, ChevronRight, ChevronLeft,
  RotateCcw, ShoppingCart, Briefcase, Church, Package, Timer,
  DollarSign, CreditCard, Banknote, Smartphone, Check, AlertCircle,
  Home, ArrowRight, Clock, Dog, Ear, Eye, HelpCircle, Truck, Accessibility
} from "lucide-react";

// Pricing constants
const PRICING = {
  base_fare: 5.00,
  per_mile: 2.25,
  minimum_fare: 15.00,
  additional_stop: 5.00,
  wait_30_min: 20.00,
  wait_60_min: 40.00,
  wait_per_30_min: 20.00
};

// Trip purpose options for Round Trip ONLY
const TRIP_PURPOSES = [
  { value: "grocery", label: "Grocery / Shopping", icon: ShoppingCart },
  { value: "work", label: "Work", icon: Briefcase },
  { value: "church", label: "Church", icon: Church },
  { value: "errands_multiple", label: "Errands / Multiple Stops", icon: Package }
];

// Payment methods
const PAYMENT_METHODS = [
  { value: "card", label: "Credit/Debit Card", icon: CreditCard, description: "Pay securely with your card" },
  { value: "cashapp", label: "Cash App", icon: Smartphone, description: "Pay via Cash App" },
  { value: "chime", label: "Chime", icon: Smartphone, description: "Pay via Chime" },
  { value: "applepay", label: "Apple Pay", icon: Smartphone, description: "Pay with Apple Pay" },
  { value: "cash", label: "Cash", icon: Banknote, description: "Pay driver in cash" }
];

// Mobility aid options
const MOBILITY_AIDS = [
  { value: "walker", label: "Walker" },
  { value: "cane", label: "Cane" },
  { value: "other", label: "Other" },
  { value: "none", label: "None" }
];

// Accessibility options
const ACCESSIBILITY_OPTIONS = [
  { value: "hearing_impaired", label: "Hearing impaired / Deaf", icon: Ear },
  { value: "visually_impaired", label: "Blind / Visually impaired", icon: Eye },
  { value: "other", label: "Other accessibility need", icon: HelpCircle },
  { value: "none", label: "None", icon: Check }
];

export default function PrivateRideWizard({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isLoading,
  selectedDate,
  setSelectedDate 
}) {
  const [step, setStep] = useState(1);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  
  // Determine if this is round trip
  const isRoundTrip = formData.service_type === "round_trip";
  // Check if Errands/Multiple Stops is selected (only for round trip)
  const isErrandsMultipleStops = isRoundTrip && formData.trip_purpose === "errands_multiple";
  
  // Steps flow:
  // ONE-WAY: 1=Trip Type, 2=Ride Details, 3=Assistance, 4=Price Review, 5=Payment
  // ROUND-TRIP: 1=Trip Type, 2=Trip Purpose, 3=Ride Details, 4=Assistance, 5=Price Review, 6=Payment
  const totalSteps = isRoundTrip ? 6 : 5;

  const filledStops = formData.stops?.filter(s => s.address)?.length || 0;
  const [routeInfo, setRouteInfo] = useState(null);

  // Calculate price - only depends on pricing-related fields, NOT addresses
  const calculatePrice = useCallback(() => {
    let breakdown = {
      base_fare: PRICING.base_fare,
      mileage: 0,
      additional_stops: 0,
      wait_time: 0,
      total: 0
    };
    
    const miles = formData.estimated_miles || 5;
    breakdown.mileage = miles * PRICING.per_mile;
    
    // Route miles already include the drive back to pickup; manual miles keep the one-way x 2 rule
    if (formData.service_type === "round_trip" && formData.miles_source !== "route") {
      breakdown.mileage = breakdown.mileage * 2;
    }
    
    const numStops = formData.stops?.filter(s => s.address)?.length || 0;
    if (numStops > 0) {
      breakdown.additional_stops = numStops * PRICING.additional_stop;
    }
    
    if (formData.wait_and_return && formData.wait_time > 0) {
      if (formData.wait_time <= 30) {
        breakdown.wait_time = PRICING.wait_30_min;
      } else if (formData.wait_time <= 60) {
        breakdown.wait_time = PRICING.wait_60_min;
      } else {
        breakdown.wait_time = PRICING.wait_60_min + 
          Math.ceil((formData.wait_time - 60) / 30) * PRICING.wait_per_30_min;
      }
    }
    
    breakdown.total = breakdown.base_fare + breakdown.mileage + 
                      breakdown.additional_stops + breakdown.wait_time;
    
    if (breakdown.total < PRICING.minimum_fare) {
      breakdown.total = PRICING.minimum_fare;
    }
    
    setPriceBreakdown(breakdown);
    
    // Only update fare_estimate if it changed to avoid unnecessary re-renders
    if (formData.fare_estimate !== breakdown.total) {
      setFormData(prev => ({ ...prev, fare_estimate: breakdown.total }));
    }
  }, [formData.service_type, formData.estimated_miles, formData.wait_and_return, 
      formData.wait_time, filledStops, formData.miles_source, formData.fare_estimate, setFormData]);

  // Calculate price on mount and when pricing factors change
  useEffect(() => {
    calculatePrice();
  }, [formData.service_type, formData.estimated_miles, formData.wait_and_return, 
      formData.wait_time, filledStops, formData.miles_source]);

  // Stable handlers using useCallback to prevent re-renders
  const handlePickupChange = useCallback((value) => {
    setFormData(prev => {
      const updated = [...prev.pickup_locations];
      updated[0] = { ...updated[0], address: value, latitude: 0, longitude: 0 };
      return { ...prev, pickup_locations: updated };
    });
  }, [setFormData]);

  const handlePickupSelect = useCallback((place) => {
    setFormData(prev => {
      const updated = [...prev.pickup_locations];
      updated[0] = { ...updated[0], ...place };
      return { ...prev, pickup_locations: updated };
    });
  }, [setFormData]);

  const handleDropoffChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, dropoff_address: value, dropoff_latitude: 0, dropoff_longitude: 0 }));
  }, [setFormData]);

  const handleDropoffSelect = useCallback((place) => {
    setFormData(prev => ({ ...prev, dropoff_address: place.address, dropoff_latitude: place.latitude, dropoff_longitude: place.longitude }));
  }, [setFormData]);

  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, client_name: value }));
  }, [setFormData]);

  const handlePhoneChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, client_phone: value }));
  }, [setFormData]);

  const handleMilesChange = useCallback((e) => {
    const value = parseFloat(e.target.value) || 0;
    setFormData(prev => ({ ...prev, estimated_miles: value, miles_source: "manual", route_outbound_miles: null, route_return_miles: null }));
  }, [setFormData]);

  const useRouteMiles = useCallback(() => {
    setFormData(prev => ({ ...prev, miles_source: null }));
  }, [setFormData]);

  const handleTimeChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, pickup_time: value }));
  }, [setFormData]);

  const handleReturnTimeChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, return_pickup_time: value, return_time_tbc: false }));
  }, [setFormData]);

  const handleReturnTbcChange = useCallback((value) => {
    const checked = !!value;
    setFormData(prev => ({ ...prev, return_time_tbc: checked, return_pickup_time: checked ? "" : prev.return_pickup_time }));
  }, [setFormData]);

  const handlePinChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setFormData(prev => ({ ...prev, pin_code: value }));
  }, [setFormData]);

  const handleSpecialRequestsChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, special_requests: value }));
  }, [setFormData]);

  const handleMobilityOtherChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, mobility_aid_other: value }));
  }, [setFormData]);

  const handleAccessibilityOtherChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, accessibility_other: value }));
  }, [setFormData]);

  const handleStopChange = useCallback((index, value) => {
    setFormData(prev => {
      const newStops = [...prev.stops];
      newStops[index] = { ...newStops[index], address: value, latitude: 0, longitude: 0 };
      return { ...prev, stops: newStops };
    });
  }, [setFormData]);

  const handleStopSelect = useCallback((index, place) => {
    setFormData(prev => {
      const newStops = [...prev.stops];
      newStops[index] = { ...newStops[index], ...place };
      return { ...prev, stops: newStops };
    });
  }, [setFormData]);

  // Full-route mileage: pickup -> destination -> each stop (-> back to pickup on round trips)
  const routeKey = JSON.stringify([
    formData.pickup_locations[0]?.address, formData.dropoff_address,
    (formData.stops || []).map(s => s.address), isRoundTrip,
  ]);
  useEffect(() => {
    if (formData.miles_source === "manual") return undefined;
    const points = [
      formData.pickup_locations[0] || {},
      { address: formData.dropoff_address, latitude: formData.dropoff_latitude, longitude: formData.dropoff_longitude },
      ...(formData.stops || []),
    ].filter(p => (p.address || "").trim().length >= 5);
    if (points.length < 2 || !(formData.pickup_locations[0]?.address || "").trim() || !(formData.dropoff_address || "").trim()) {
      setRouteInfo(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setRouteInfo({ loading: true });
      try {
        const { data } = await apiClient.post("/route/estimate", {
          round_trip: isRoundTrip,
          points: points.map(p => ({ address: p.address, latitude: p.latitude || null, longitude: p.longitude || null })),
        });
        if (cancelled) return;
        setRouteInfo(data);
        const coords = Object.fromEntries(data.points.map(p => [p.address, p]));
        setFormData(prev => {
          const fill = (obj, latKey = "latitude", lngKey = "longitude", addr = obj.address) =>
            coords[addr] && !obj[latKey] ? { [latKey]: coords[addr].latitude, [lngKey]: coords[addr].longitude } : {};
          const pickup = prev.pickup_locations[0] || {};
          return {
            ...prev,
            estimated_miles: Math.max(data.total_miles, 0.1),
            route_outbound_miles: data.outbound_miles,
            route_return_miles: data.return_miles,
            miles_source: prev.miles_source === "manual" ? "manual" : "route",
            pickup_locations: [{ ...pickup, ...fill(pickup) }, ...prev.pickup_locations.slice(1)],
            ...fill(prev, "dropoff_latitude", "dropoff_longitude", prev.dropoff_address),
            stops: (prev.stops || []).map(s => ({ ...s, ...fill(s) })),
          };
        });
      } catch (err) {
        if (!cancelled) setRouteInfo({ error: err.response?.data?.detail || "Couldn't calculate the route. Please enter the miles." });
      }
    }, 1200);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, formData.miles_source === "manual"]);

  const canProceed = () => {
    if (isRoundTrip) {
      switch(step) {
        case 1: return formData.service_type === "round_trip";
        case 2: return formData.trip_purpose;
        case 3: return formData.client_name && formData.client_phone && 
                 formData.pickup_locations[0]?.address && formData.dropoff_address &&
                 formData.pickup_date && formData.pickup_time && 
                 formData.pin_code?.length === 4 && formData.vehicle_type;
        case 4: return true;
        case 5: return true;
        case 6: return formData.payment_method;
        default: return false;
      }
    } else {
      switch(step) {
        case 1: return formData.service_type === "one_way";
        case 2: return formData.client_name && formData.client_phone && 
                 formData.pickup_locations[0]?.address && formData.dropoff_address &&
                 formData.pickup_date && formData.pickup_time && 
                 formData.pin_code?.length === 4 && formData.vehicle_type;
        case 3: return true;
        case 4: return true;
        case 5: return formData.payment_method;
        default: return false;
      }
    }
  };

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleAddStop = () => {
    setFormData(prev => ({
      ...prev,
      has_multiple_stops: true,
      stops: [...(prev.stops || []), { address: "", wait: false, wait_minutes: 0 }]
    }));
  };

  const handleRemoveStop = (index) => {
    setFormData(prev => {
      const stops = prev.stops.filter((_, i) => i !== index);
      return { ...prev, stops, has_multiple_stops: stops.length > 0 };
    });
  };

  const handleSubmitRide = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const getStepLabel = () => {
    if (isRoundTrip) {
      switch(step) {
        case 1: return "Trip Type";
        case 2: return "Trip Purpose";
        case 3: return "Ride Details";
        case 4: return "Assistance";
        case 5: return "Price Review";
        case 6: return "Payment";
        default: return "";
      }
    } else {
      switch(step) {
        case 1: return "Trip Type";
        case 2: return "Ride Details";
        case 3: return "Assistance";
        case 4: return "Price Review";
        case 5: return "Payment";
        default: return "";
      }
    }
  };

  // Determine which step shows which content
  const rideDetailsStep = isRoundTrip ? 3 : 2;
  const assistanceStep = isRoundTrip ? 4 : 3;
  const priceReviewStep = isRoundTrip ? 5 : 4;
  const paymentStep = isRoundTrip ? 6 : 5;

  return (
    <div className="space-y-4" data-testid="private-ride-wizard">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">Step {step} of {totalSteps}</span>
          <span className="text-sm text-slate-500">{getStepLabel()}</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>
      
      {/* Step 1: Service Type (One-Way or Round-Trip) */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-slate-800">What type of trip?</h3>
            <p className="text-slate-500 text-sm mt-1">Choose your trip type</p>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            <button
              type="button"
              onClick={() => setFormData({ 
                ...formData, 
                service_type: "one_way", 
                trip_purpose: "", 
                has_multiple_stops: false, 
                stops: [], 
                wait_and_return: false, 
                wait_time: 0 
              })}
              className={`p-6 rounded-2xl border-2 transition-all text-left ${
                formData.service_type === "one_way"
                  ? "border-orange-500 bg-orange-50 shadow-lg"
                  : "border-slate-200 hover:border-orange-200 bg-white"
              }`}
              data-testid="trip-type-one-way"
            >
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  formData.service_type === "one_way" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"
                }`}>
                  <ArrowRight className="w-8 h-8" />
                </div>
                <div>
                  <p className={`text-lg font-bold ${formData.service_type === "one_way" ? "text-orange-700" : "text-slate-700"}`}>
                    One Way
                  </p>
                  <p className="text-sm text-slate-500">Go from point A to point B</p>
                </div>
                {formData.service_type === "one_way" && (
                  <Check className="w-6 h-6 text-orange-500 ml-auto" />
                )}
              </div>
            </button>
            
            <button
              type="button"
              onClick={() => setFormData({ ...formData, service_type: "round_trip", trip_purpose: "" })}
              className={`p-6 rounded-2xl border-2 transition-all text-left ${
                formData.service_type === "round_trip"
                  ? "border-orange-500 bg-orange-50 shadow-lg"
                  : "border-slate-200 hover:border-orange-200 bg-white"
              }`}
              data-testid="trip-type-round-trip"
            >
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  formData.service_type === "round_trip" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"
                }`}>
                  <RotateCcw className="w-8 h-8" />
                </div>
                <div>
                  <p className={`text-lg font-bold ${formData.service_type === "round_trip" ? "text-orange-700" : "text-slate-700"}`}>
                    Round Trip
                  </p>
                  <p className="text-sm text-slate-500">Go somewhere and come back home</p>
                </div>
                {formData.service_type === "round_trip" && (
                  <Check className="w-6 h-6 text-orange-500 ml-auto" />
                )}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Step 2 for Round Trip: Trip Purpose */}
      {isRoundTrip && step === 2 && (
        <div className="space-y-4">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-slate-800">Trip Purpose</h3>
            <p className="text-slate-500 text-sm mt-1">What is this trip for?</p>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {TRIP_PURPOSES.map((purpose) => (
              <button
                key={purpose.value}
                type="button"
                onClick={() => {
                  const isErrands = purpose.value === "errands_multiple";
                  setFormData({ 
                    ...formData, 
                    trip_purpose: purpose.value,
                    has_multiple_stops: isErrands,
                    stops: isErrands ? [{ address: "", wait: false, wait_minutes: 0 }] : [],
                    wait_and_return: isErrands ? false : formData.wait_and_return,
                    wait_time: isErrands ? 0 : formData.wait_time
                  });
                }}
                className={`p-5 rounded-2xl border-2 transition-all text-left ${
                  formData.trip_purpose === purpose.value
                    ? "border-orange-500 bg-orange-50 shadow-lg"
                    : "border-slate-200 hover:border-orange-200 bg-white"
                }`}
                data-testid={`trip-purpose-${purpose.value}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    formData.trip_purpose === purpose.value ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    <purpose.icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold ${formData.trip_purpose === purpose.value ? "text-orange-700" : "text-slate-700"}`}>
                      {purpose.label}
                    </p>
                    {purpose.value === "errands_multiple" && (
                      <p className="text-xs text-slate-500">Add multiple destinations</p>
                    )}
                  </div>
                  {formData.trip_purpose === purpose.value && (
                    <Check className="w-6 h-6 text-orange-500" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ride Details Step - INLINE JSX to prevent focus loss */}
      {step === rideDetailsStep && (
        <div className="space-y-5">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-slate-800">Ride Details</h3>
            <p className="text-slate-500 text-sm mt-1">Enter your trip information</p>
          </div>
          
          {/* Name & Phone */}
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label className="font-semibold">Client Name</Label>
              <Input
                value={formData.client_name}
                onChange={handleNameChange}
                placeholder="Enter full name"
                className="h-12 text-lg"
                required
                data-testid="client-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">Phone Number</Label>
              <Input
                value={formData.client_phone}
                onChange={handlePhoneChange}
                placeholder="(555) 123-4567"
                className="h-12 text-lg"
                required
                data-testid="client-phone-input"
              />
            </div>
          </div>
          
          {/* Route: Pickup -> Destination -> Stops (Uber-style) */}
          <div className="space-y-3" data-testid="route-address-list">
            <div className="space-y-2">
              <Label className="font-semibold flex items-center gap-2">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <Home className="w-3 h-3 text-green-600" />
                </div>
                Pickup Address
              </Label>
              <AddressAutocomplete
                value={formData.pickup_locations[0]?.address || ""}
                onChange={handlePickupChange}
                onSelect={handlePickupSelect}
                placeholder="Enter pickup address"
                testId="pickup-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-semibold flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-3 h-3 text-blue-600" />
                </div>
                {isRoundTrip ? "Destination Address" : "Drop-off Address"}
              </Label>
              <AddressAutocomplete
                value={formData.dropoff_address}
                onChange={handleDropoffChange}
                onSelect={handleDropoffSelect}
                placeholder="Where are you going?"
                testId="dropoff-address-input"
              />
            </div>

            {formData.stops?.map((stop, index) => (
              <div key={`stop-${index}`} className="space-y-2" data-testid={`stop-row-${index}`}>
                <Label className="font-semibold flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-[10px] font-bold text-blue-600">{index + 2}</span>
                  </div>
                  Stop {index + 2}
                </Label>
                <AddressAutocomplete
                  value={stop.address}
                  onChange={(v) => handleStopChange(index, v)}
                  onSelect={(place) => handleStopSelect(index, place)}
                  placeholder="Enter next address"
                  testId={`stop-${index}-input`}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => handleRemoveStop(index)}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-150"
                      aria-label={`Remove stop ${index + 2}`}
                      data-testid={`remove-stop-${index}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  }
                />
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddStop}
              className="flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-900 py-1 transition-colors duration-150"
              data-testid="add-stop-button"
            >
              <Plus className="w-4 h-4" /> Add Another Stop
            </button>
          </div>

          {/* Wait & Return (Round Trip only, non-errands) */}
          {isRoundTrip && !isErrandsMultipleStops && (
            <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
              <Label className="font-semibold flex items-center gap-2">
                <Timer className="w-4 h-4 text-orange-500" />
                Wait & Return Service
              </Label>
              <p className="text-sm text-slate-500">Should the driver wait while you're at your destination?</p>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, wait_and_return: false, wait_time: 0 })}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    !formData.wait_and_return
                      ? "border-green-500 bg-green-50"
                      : "border-slate-200 bg-white"
                  }`}
                  data-testid="wait-return-no"
                >
                  <p className="font-semibold text-sm">No Wait Needed</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, wait_and_return: true, wait_time: 30 })}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    formData.wait_and_return
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 bg-white"
                  }`}
                  data-testid="wait-return-yes"
                >
                  <p className="font-semibold text-sm">Wait & Return</p>
                </button>
              </div>
              
              {formData.wait_and_return && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { value: 30, label: "30 min", price: "$20" },
                    { value: 60, label: "1 hour", price: "$40" },
                    { value: 90, label: "1.5 hrs", price: "$60" }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, wait_time: option.value })}
                      className={`p-2 rounded-lg border-2 text-center ${
                        formData.wait_time === option.value
                          ? "border-orange-500 bg-orange-100"
                          : "border-slate-200 bg-white"
                      }`}
                      data-testid={`wait-time-${option.value}`}
                    >
                      <p className="text-sm font-medium">{option.label}</p>
                      <p className="text-xs text-orange-600">{option.price}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Estimated Miles */}
          <div className="space-y-2">
            <Label className="font-semibold">Estimated Distance (miles)</Label>
            <Input
              type="number"
              value={formData.estimated_miles || ""}
              onChange={handleMilesChange}
              placeholder="Approximate miles"
              className="h-12"
              min="1"
              data-testid="estimated-miles-input"
            />
            <p className="text-xs text-slate-500" data-testid="route-miles-status">
              {routeInfo?.loading && "Calculating your full route…"}
              {routeInfo?.error && <span className="text-amber-600">{routeInfo.error}</span>}
              {!routeInfo?.loading && !routeInfo?.error && formData.miles_source === "route" &&
                `Calculated from your full route${routeInfo?.points?.length > 2 ? ` with ${routeInfo.points.length - 2} extra stop${routeInfo.points.length > 3 ? "s" : ""}` : ""}${isRoundTrip ? ", including the drive back to pickup" : ""}. You can edit it.`}
              {formData.miles_source === "manual" && (
                <>Entered manually{isRoundTrip ? " (one-way miles, doubled for the return)" : ""}. <button type="button" onClick={useRouteMiles} className="underline text-teal-700" data-testid="use-route-miles-btn">Recalculate from route</button></>
              )}
              {!routeInfo && !formData.miles_source && "Calculated automatically once addresses are entered"}
            </p>
          </div>
          
          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="font-semibold">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-12 justify-start text-left" data-testid="date-picker-trigger">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "MMM d") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setFormData({ ...formData, pickup_date: date ? format(date, "yyyy-MM-dd") : "" });
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">Time</Label>
              <Input
                type="time"
                value={formData.pickup_time}
                onChange={handleTimeChange}
                className="h-12"
                required
                data-testid="pickup-time-input"
              />
            </div>
          </div>

          {/* Return Pickup Time (Round Trip without Wait & Return) */}
          {isRoundTrip && !formData.wait_and_return && (
            <div className="space-y-2 p-4 bg-orange-50 rounded-xl border border-orange-200" data-testid="return-time-section">
              <Label className="font-semibold flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-orange-500" />
                Return Pickup Time
              </Label>
              <Input
                type="time"
                value={formData.return_pickup_time || ""}
                onChange={handleReturnTimeChange}
                disabled={!!formData.return_time_tbc}
                className="h-12"
                data-testid="return-time-input"
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={!!formData.return_time_tbc}
                  onCheckedChange={handleReturnTbcChange}
                  className="rounded-[4px] border-orange-500 data-[state=checked]:bg-orange-500 data-[state=checked]:text-white"
                  data-testid="return-time-tbc-checkbox"
                />
                Return Time To Be Confirmed
              </label>
              <p className="text-xs text-slate-500">Leave blank or tick the box if you don't know when you'll be ready.</p>
            </div>
          )}

          {/* Vehicle Type */}
          <div className="space-y-3">
            <Label className="font-semibold">Vehicle Type</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, vehicle_type: "car" })}
                className={`p-3 rounded-xl border-2 transition-all text-center ${
                  formData.vehicle_type === "car"
                    ? "border-orange-500 bg-orange-50"
                    : "border-slate-200 bg-white"
                }`}
                data-testid="vehicle-car"
              >
                <Car className="w-6 h-6 mx-auto mb-1 text-slate-700" />
                <p className="text-sm font-medium">Car</p>
                <p className="text-xs text-green-600">Available</p>
              </button>
              <button
                type="button"
                disabled
                className="p-3 rounded-xl border-2 border-slate-200 bg-slate-50 text-center opacity-60 cursor-not-allowed"
                data-testid="vehicle-van"
              >
                <Truck className="w-6 h-6 mx-auto mb-1 text-slate-400" />
                <p className="text-sm font-medium text-slate-400">Van</p>
                <p className="text-xs text-slate-400">Coming Soon</p>
              </button>
              <button
                type="button"
                disabled
                className="p-3 rounded-xl border-2 border-slate-200 bg-slate-50 text-center opacity-60 cursor-not-allowed"
                data-testid="vehicle-wheelchair-van"
              >
                <Accessibility className="w-6 h-6 mx-auto mb-1 text-slate-400" />
                <p className="text-sm font-medium text-slate-400">Wheelchair Van</p>
                <p className="text-xs text-slate-400">Coming Soon</p>
              </button>
            </div>
          </div>
          
          {/* PIN Code */}
          <div className="space-y-2">
            <Label className="font-semibold">4-Digit PIN Code</Label>
            <Input
              value={formData.pin_code}
              onChange={handlePinChange}
              placeholder="1234"
              maxLength={4}
              className="h-12 text-center text-2xl tracking-widest"
              required
              data-testid="pin-code-input"
            />
            <p className="text-xs text-slate-500">Driver will confirm this PIN at pickup</p>
          </div>
        </div>
      )}

      {/* Assistance Step - INLINE JSX */}
      {step === assistanceStep && (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-slate-800">Assistance & Accessibility</h3>
            <p className="text-slate-500 text-sm mt-1">Help us serve you better</p>
          </div>

          {/* Entry Assistance */}
          <div className="space-y-3">
            <Label className="font-semibold">Does the client need assistance getting into or out of the vehicle?</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, needs_entry_assistance: true })}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  formData.needs_entry_assistance === true
                    ? "border-orange-500 bg-orange-50"
                    : "border-slate-200 bg-white hover:border-orange-200"
                }`}
                data-testid="entry-assist-yes"
              >
                <p className="font-semibold">Yes</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, needs_entry_assistance: false })}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  formData.needs_entry_assistance === false
                    ? "border-green-500 bg-green-50"
                    : "border-slate-200 bg-white hover:border-green-200"
                }`}
                data-testid="entry-assist-no"
              >
                <p className="font-semibold">No</p>
              </button>
            </div>
          </div>

          {/* Mobility Aid */}
          <div className="space-y-3">
            <Label className="font-semibold">Does the client use a mobility aid?</Label>
            <div className="grid grid-cols-2 gap-2">
              {MOBILITY_AIDS.map((aid) => (
                <button
                  key={aid.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, mobility_aid: aid.value, mobility_aid_other: aid.value !== "other" ? "" : formData.mobility_aid_other })}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    formData.mobility_aid === aid.value
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 bg-white hover:border-orange-200"
                  }`}
                  data-testid={`mobility-aid-${aid.value}`}
                >
                  <p className="font-medium">{aid.label}</p>
                </button>
              ))}
            </div>
            {formData.mobility_aid === "other" && (
              <Input
                placeholder="Please describe the mobility aid"
                value={formData.mobility_aid_other || ""}
                onChange={handleMobilityOtherChange}
                className="mt-2"
                data-testid="mobility-aid-other-input"
              />
            )}
          </div>

          {/* Accessibility Needs */}
          <div className="space-y-3">
            <Label className="font-semibold">Does the client have any accessibility needs?</Label>
            <div className="grid grid-cols-1 gap-2">
              {ACCESSIBILITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, accessibility_need: option.value, accessibility_other: option.value !== "other" ? "" : formData.accessibility_other })}
                  className={`p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                    formData.accessibility_need === option.value
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 bg-white hover:border-orange-200"
                  }`}
                  data-testid={`accessibility-${option.value}`}
                >
                  <option.icon className="w-5 h-5 text-slate-600" />
                  <p className="font-medium">{option.label}</p>
                </button>
              ))}
            </div>
            {formData.accessibility_need === "other" && (
              <Input
                placeholder="Please describe the accessibility need"
                value={formData.accessibility_other || ""}
                onChange={handleAccessibilityOtherChange}
                className="mt-2"
                data-testid="accessibility-other-input"
              />
            )}
          </div>

          {/* Service Animal */}
          <div className="space-y-3">
            <Label className="font-semibold flex items-center gap-2">
              <Dog className="w-5 h-5 text-slate-600" />
              Does the client have a service animal?
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, has_service_animal: true })}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  formData.has_service_animal === true
                    ? "border-orange-500 bg-orange-50"
                    : "border-slate-200 bg-white hover:border-orange-200"
                }`}
                data-testid="service-animal-yes"
              >
                <p className="font-semibold">Yes</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, has_service_animal: false })}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  formData.has_service_animal === false
                    ? "border-green-500 bg-green-50"
                    : "border-slate-200 bg-white hover:border-green-200"
                }`}
                data-testid="service-animal-no"
              >
                <p className="font-semibold">No</p>
              </button>
            </div>
            {formData.has_service_animal && (
              <p className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                Service animal information will be shared with the driver.
              </p>
            )}
          </div>

          {/* Additional Notes */}
          <div className="space-y-2">
            <Label className="font-semibold">Additional ride notes (optional)</Label>
            <Textarea
              placeholder="Any additional information the driver should know..."
              value={formData.special_requests || ""}
              onChange={handleSpecialRequestsChange}
              rows={3}
              data-testid="special-requests-input"
            />
          </div>
        </div>
      )}

      {/* Price Review Step */}
      {step === priceReviewStep && (
        <div className="space-y-5">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-slate-800">Your Ride Price</h3>
            <p className="text-slate-500 text-sm mt-1">Review your price before payment</p>
          </div>
          
          {/* Trip Summary */}
          <div className="bg-slate-50 rounded-xl p-4 mb-4">
            <p className="text-sm font-medium text-slate-600 mb-2">Trip Summary</p>
            <p className="text-sm text-slate-700">
              <span className="font-semibold">{isRoundTrip ? "Round Trip" : "One-Way Trip"}</span>
              {isRoundTrip && formData.trip_purpose && ` - ${TRIP_PURPOSES.find(p => p.value === formData.trip_purpose)?.label}`}
            </p>
            <p className="text-sm text-slate-600 mt-1">
              {formData.pickup_locations[0]?.address}
              {" → "}{formData.dropoff_address}
              {formData.stops?.filter(s => s.address).map((s) => ` → ${s.address}`)}
              {isRoundTrip && " → Back Home"}
            </p>
          </div>
          
          {/* Price Breakdown */}
          <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-5 h-5 text-orange-600" />
                <h4 className="font-bold text-lg text-slate-800">Ride Price Breakdown</h4>
              </div>
              
              {priceBreakdown && (
                <div className="space-y-3">
                  <div className="flex justify-between text-slate-700">
                    <span>Base Fare</span>
                    <span className="font-medium">${priceBreakdown.base_fare.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between text-slate-700">
                    <span>Mileage ({formData.estimated_miles || 5} mi{formData.miles_source === "route" ? " full route" : isRoundTrip ? " × 2" : ""} × $2.25)</span>
                    <span className="font-medium">${priceBreakdown.mileage.toFixed(2)}</span>
                  </div>
                  
                  {priceBreakdown.additional_stops > 0 && (
                    <div className="flex justify-between text-slate-700">
                      <span>Additional Stops ({formData.stops?.filter(s => s.address)?.length || 0} × $5)</span>
                      <span className="font-medium">${priceBreakdown.additional_stops.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {priceBreakdown.wait_time > 0 && (
                    <div className="flex justify-between text-slate-700">
                      <span>Wait & Return ({formData.wait_time} min)</span>
                      <span className="font-medium">${priceBreakdown.wait_time.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="border-t-2 border-dashed border-orange-300 pt-3 mt-3">
                    <div className="flex justify-between text-xl font-bold">
                      <span className="text-slate-800">Total Ride Price</span>
                      <span className="text-orange-600">${priceBreakdown.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          <p className="text-xs text-slate-500 text-center">
            * Final fare may vary based on actual mileage{isRoundTrip && formData.wait_and_return ? " and wait time" : ""}
          </p>
        </div>
      )}

      {/* Payment Step */}
      {step === paymentStep && (
        <div className="space-y-5">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-slate-800">How will you pay?</h3>
            <p className="text-slate-500 text-sm mt-1">Select your payment method</p>
          </div>
          
          {/* Total Due */}
          <div className="p-4 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl mb-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-slate-700">Total Due:</span>
              <span className="text-2xl font-bold text-orange-600">${priceBreakdown?.total.toFixed(2)}</span>
            </div>
          </div>
          
          <div className="space-y-3">
            {PAYMENT_METHODS.map((method) => (
              <button
                key={method.value}
                type="button"
                onClick={() => setFormData({ ...formData, payment_method: method.value })}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  formData.payment_method === method.value
                    ? "border-orange-500 bg-orange-50 shadow-md"
                    : "border-slate-200 hover:border-orange-200 bg-white"
                }`}
                data-testid={`payment-${method.value}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.payment_method === method.value ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    <method.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold ${formData.payment_method === method.value ? "text-orange-700" : "text-slate-700"}`}>
                      {method.label}
                    </p>
                    <p className="text-xs text-slate-500">{method.description}</p>
                  </div>
                  {formData.payment_method === method.value && (
                    <Check className="w-6 h-6 text-orange-500" />
                  )}
                </div>
              </button>
            ))}
          </div>
          
          {/* Cash Warning */}
          {formData.payment_method === "cash" && (
            <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-amber-800">Cash Payment Notice</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Cash payment of <span className="font-bold">${priceBreakdown?.total.toFixed(2)}</span> must be given to the driver <span className="font-bold">before</span> the driver starts the trip.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-3 pt-4">
        {step > 1 && (
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="flex-1 h-14 text-lg"
            data-testid="wizard-back-button"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        )}
        
        {step < totalSteps ? (
          <Button
            type="button"
            onClick={handleNext}
            disabled={!canProceed()}
            className="flex-1 h-14 text-lg bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            data-testid="wizard-next-button"
          >
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        ) : (
          <Button
            type="submit"
            onClick={handleSubmitRide}
            disabled={!canProceed() || isLoading}
            className="flex-1 h-14 text-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            data-testid="wizard-confirm-button"
          >
            {isLoading ? (
              <>
                <Clock className="w-5 h-5 mr-2 animate-spin" />
                Booking...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Confirm Ride
              </>
            )}
          </Button>
        )}
      </div>
      
      {/* Cancel Link */}
      <button
        type="button"
        onClick={onCancel}
        className="w-full text-center text-slate-500 hover:text-slate-700 text-sm py-2"
        data-testid="wizard-cancel-button"
      >
        Cancel
      </button>
    </div>
  );
}
